import { useTranslation } from 'react-i18next';
import {
  ShoppingCart,
  Briefcase,
  Home,
  BookOpen,
  Heart,
  Users,
  BriefcaseBusiness,
} from 'lucide-react';
import FadeIn from './FadeIn';
import Card from './Card';
import ComingSoonBadge from './ComingSoonBadge';

/**
 * Services Component
 * 
 * Displays all available services offered by Suersdey in a responsive grid layout.
 * Services include:
 * - Marketplace: Buy and sell within local communities
 * - Business Directory: Discover and connect with local businesses
 * - Property Listings: Real estate search and rental management
 * - Education Hub: Schools, courses, and educational resources
 * - Charity Platform: Connect charities with volunteer opportunities
 * - Jobs Board: Job listings and employer connections
 * - Jobs Request: Post job requests (Coming Soon)
 * 
 * Each service card displays:
 * - Colored icon representing the service
 * - Service name and description
 * - Key features list
 * - Coming Soon badge for unreleased services
 * 
 * Features:
 * - Responsive grid layout (1 column mobile, 2 tablet, 3 desktop)
 * - Staggered fade-in animations
 * - Hover effects with scale transform
 * - Bilingual content support
 * 
 * @component
 * @example
 * ```tsx
 * <Services />
 * ```
 */

const iconMap = {
  'shopping-cart': ShoppingCart,
  'briefcase': Briefcase,
  'home': Home,
  'book': BookOpen,
  'heart': Heart,
  'briefcase-business': BriefcaseBusiness,
  'users': Users,
};

const colorMap = {
  marketplace: 'text-primary-600 bg-primary-50',
  business: 'text-secondary-600 bg-secondary-50',
  property: 'text-accent-600 bg-accent-50',
  education: 'text-primary-700 bg-primary-100',
  charity: 'text-error bg-red-50',
  jobs: 'text-secondary-700 bg-secondary-100',
  jobsRequest: 'text-accent-700 bg-accent-100',
};

export default function Services() {
  const { t } = useTranslation('home');

  const services = [
    {
      key: 'marketplace',
      icon: 'shopping-cart',
      color: colorMap.marketplace,
    },
    {
      key: 'business',
      icon: 'briefcase',
      color: colorMap.business,
    },
    {
      key: 'property',
      icon: 'home',
      color: colorMap.property,
    },
    {
      key: 'education',
      icon: 'book',
      color: colorMap.education,
    },
    {
      key: 'charity',
      icon: 'heart',
      color: colorMap.charity,
    },
    {
      key: 'jobs',
      icon: 'briefcase-business',
      color: colorMap.jobs,
    },
    {
      key: 'jobsRequest',
      icon: 'users',
      color: colorMap.jobsRequest,
      comingSoon: true,
    },
  ];

  return (
    <section id="services" className="section-padding bg-neutral-50">
      <div className="container-custom">
        {/* Section Header */}
        <FadeIn>
          <div className="text-center mb-12 max-w-3xl mx-auto">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-neutral-900 mb-4">
              {t('services.title')}
            </h2>
            <p className="text-xl bg-gradient-to-r from-primary-600 to-accent-600 bg-clip-text text-transparent font-semibold mb-4">
              {t('services.subtitle')}
            </p>
            <p className="text-neutral-700">
              {t('services.description')}
            </p>
          </div>
        </FadeIn>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => {
            const Icon = iconMap[service.icon as keyof typeof iconMap];
            const features = t(`services.items.${service.key}.features`, {
              returnObjects: true,
            }) as string[];

            return (
              <FadeIn key={service.key} delay={100 + index * 50}>
                <Card hover className="h-full transition-transform hover:scale-105">
                {/* Icon */}
                <div className={`w-16 h-16 rounded-xl ${service.color} flex items-center justify-center mb-4`}>
                  <Icon size={32} />
                </div>

                {/* Title */}
                <h3 className="text-xl font-semibold text-neutral-900 mb-2 flex items-center gap-2 flex-wrap">
                  <span>{t(`services.items.${service.key}.name`)}</span>
                  {service.comingSoon && (
                    <ComingSoonBadge text={t(`services.items.${service.key}.comingSoon`)} />
                  )}
                </h3>

                {/* Description */}
                <p className="text-neutral-600 text-sm mb-4">
                  {t(`services.items.${service.key}.description`)}
                </p>

                {/* Features */}
                <ul className="space-y-2">
                  {features.map((feature, index) => (
                    <li key={index} className="text-sm text-neutral-700 flex items-start gap-2">
                      <span className="text-primary-500 mt-1">✓</span>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            </FadeIn>
            );
          })}
        </div>
      </div>
    </section>
  );
}
